/**
 * @experimental Lib-owned chat vocabulary for the reusable AI chat component.
 * Shape may change until validated against a second, structurally-different transport.
 */

export type ChatContentType = 'REASONING' | string;

export interface ChatQuestion {
  id: string;
  text: string;
  type: string;
  options?: string[];
}

/**
 * Inbound events a transport emits. The store folds these into `ChatMessage`s.
 * Interactive variants (`plan`, `questions`, `proposed-action`, `input-validation`)
 * are declared now but not rendered in rev 1.
 */
export type ChatEvent =
  | { kind: 'text'; delta: string; messageId?: string; contentType?: ChatContentType }
  | { kind: 'tool-start'; id: string; name: string; description?: string; args?: string }
  | { kind: 'tool-progress'; id: string; status?: string; progressMessage?: string }
  | { kind: 'tool-complete'; id: string; result?: string; latency?: string }
  | { kind: 'final' }
  | { kind: 'error'; message: string; code?: string }
  | { kind: 'plan'; id: string; output: string; plan: string }
  | { kind: 'questions'; id: string; output: string; questions: ChatQuestion[] }
  | { kind: 'proposed-action'; id: string; output: string; action: Record<string, unknown> }
  | { kind: 'input-validation'; valid: boolean; message?: string };

export type ChatEventKind = ChatEvent['kind'];

/** Outbound input a host sends through the transport. */
export type ChatInput =
  | { kind: 'text'; text: string }
  | { kind: 'action'; actionId: string; payload: Record<string, unknown> };

export type ToolCallState = 'running' | 'complete';

export interface ToolCall {
  id: string;
  name: string;
  state: ToolCallState;
  description?: string;
  args?: string;
  status?: string;
  progressMessage?: string;
  result?: string;
  latency?: string;
}

/**
 * An ordered, typed content item within a message. The store folds the event stream into
 * blocks in transmission order; each kind renders via its own block component. New response
 * kinds (image, chart, diagram, …) are added here and given a matching component.
 */
export interface TextBlock {
  kind: 'text';
  id: string;
  text: string;
}

export interface ToolBlock {
  kind: 'tool';
  id: string;
  tool: ToolCall;
}

export type ChatBlock = TextBlock | ToolBlock;
export type ChatBlockKind = ChatBlock['kind'];

export type ChatMessageRole = 'user' | 'assistant';
export type ChatMessageStatus = 'streaming' | 'complete' | 'error';

/** Renderable view-model the store exposes and `<ai-chat>` renders as an ordered list of blocks. */
export interface ChatMessage {
  id: string;
  role: ChatMessageRole;
  status: ChatMessageStatus;
  blocks: ChatBlock[];
  errorMessage?: string;
}
